/*
  Audience rules for products (family activities vs. generic events).

  A product arriving from the BE carries isGenericEvent (Product2.Generic_Event__c).
  Generic events sell adult tickets only: the child counter is hidden, the
  stored visitors are normalized to zero children, and the date-first flow
  never offers them (the BE also filters them out of /getCampaigns).
*/

export const isGenericEventProduct = (product) => product?.isGenericEvent === true;

/*
  The visitors correction to dispatch when the selected product changes.
  Returns a patch for setVisitors, or null when no correction is needed.
  - generic-event product: children must drop to 0 (the shopper's count is
    HELD by the caller so later family purchases keep it - the up-front
    visitors selection is meant to carry across products in one session).
  - family product in the public flow: a zeroed child count is restored to
    the held count (or the one-child minimum when nothing was held).
  - internal (box office) mode keeps its freedom to sell any split.
*/
export const visitorsPatchForProduct = (product, visitors, isInternalMode, heldUnderAge, lpGenericOnly) => {
    const underAge = visitors?.underAge || 0;
    /*
      A generic-only LP is adult-only no matter what is (or was) selected -
      a stale selectedActivity from another LP (localStorage) must not
      resurrect the child count through the restore rule below.
    */
    if (lpGenericOnly) {
        return underAge > 0 ? { underAge: 0 } : null;
    }
    if (!product) return null;
    if (isGenericEventProduct(product)) {
        return underAge > 0 ? { underAge: 0 } : null;
    }
    if (!isInternalMode && underAge === 0) {
        return { underAge: heldUnderAge || 1 };
    }
    return null;
};

/*
  An LP whose products are ALL generic events: the whole flow is adult-only,
  so the children counter is hidden from the very first step. Mixed LPs are a
  content-policy no-no (no current use case) but degrade gracefully per item.
*/
export const isGenericOnlyLp = (products) =>
    (products || []).length > 0 && products.every((product) => isGenericEventProduct(product));

/* Participants line for the order summary. */
export const participantsSummary = (visitors, isGenericEvent) =>
    isGenericEvent
        ? `${visitors?.adults || 0} מבוגרים`
        : `${visitors?.underAge || '--'} ילדים, ${visitors?.adults || '--'} מבוגרים`;
